import { useEffect, useRef, useState } from 'react';
import { Button } from './Button.js';
import { QuietAction } from './QuietAction.js';
import { biggerShowFor } from './biggerShow.js';
import type { GameFit } from '../assets/manifest.js';
import { useRoom } from '../net/useRoom.js';
import { trackClient } from '../net/telemetry.js';

/**
 * The "five is enough" upsell — the one moment that tells a table it has grown
 * into the flagship.
 *
 * Rooms are born `onenight` (2026-08-09), which fixed small-table entry and
 * silently removed the only prompt that ever mentioned Blood Moon to a group
 * that assembled five seats. Two honest surfaces, both host-only, no forced
 * switch and no countdown: the LOBBY moment (this component) and a second
 * action on One Night's END (the hook alone — each END screen renders it in its
 * own action-row idiom).
 *
 * The rule itself lives in `biggerShow.ts` (pure + unit-tested); everything
 * here is the once-per-room memory and the two telemetry beacons.
 */

/** Which surface a given CTA is mounted on — rides along on both beacons. */
export type BiggerShowSurface = 'lobby' | 'end';

/** Per-room suppression key: the lobby moment is offered ONCE per room. */
const seenKey = (code: string) => `rm-bigger-show:${code}`;

function alreadyOffered(code: string): boolean {
  try {
    return sessionStorage.getItem(seenKey(code)) === '1';
  } catch {
    return false;
  }
}

function markOffered(code: string): void {
  try {
    sessionStorage.setItem(seenKey(code), '1');
  } catch {
    /* private mode / no storage — the CTA simply may reappear next lobby */
  }
}

export interface BiggerShowOptions {
  code: string;
  gameId: string;
  playerCount: number;
  isHost: boolean;
  surface: BiggerShowSurface;
  /**
   * Gate for a surface that mounts before it shows anything (the END screen
   * holds a suspense curtain first). `false` keeps the offer — and its
   * `bm_upsell_shown` beacon — pending, so "shown" always means SEEN.
   */
  enabled?: boolean;
}

/**
 * The decision + the two beacons, without any chrome.
 *
 * Returns `null` when there is nothing to offer (not the host, no bigger game
 * fits the crowd, or — on the lobby surface — this room was already offered
 * one). `act()` fires `bm_upsell_acted` and sends the host-gated SWITCH_GAME
 * that the "Change game" picker sends, so every seat is preserved; on END the
 * switch re-inits the room into the new game's LOBBY, where the host still has
 * to raise the curtain.
 *
 * `bm_upsell_shown` fires once per mount, the first render that has something
 * to offer. On the lobby surface that is also when the room is marked as
 * offered, so the moment is spent whether or not it is taken — the END CTA is
 * what catches a host who was busy starting the game.
 */
export function useBiggerShow({
  code,
  gameId,
  playerCount,
  isHost,
  surface,
  enabled = true,
}: BiggerShowOptions): {
  fit: GameFit;
  act: () => void;
  dismiss: () => void;
} | null {
  const send = useRoom((s) => s.send);
  const [dismissed, setDismissed] = useState(false);
  // Consulted once per mount: marking the room as offered below must not make
  // the CTA vanish from under the host who is looking at it.
  const suppressed = useRef(surface === 'lobby' && alreadyOffered(code));
  const shown = useRef(false);

  const candidate =
    enabled && isHost && !dismissed && !suppressed.current
      ? biggerShowFor(gameId, playerCount)
      : null;

  useEffect(() => {
    if (!candidate || shown.current) return;
    shown.current = true;
    if (surface === 'lobby') markOffered(code);
    trackClient('bm_upsell_shown', {
      surface,
      from: gameId,
      to: candidate.id,
      players: playerCount,
    });
  }, [candidate, surface, code, gameId, playerCount]);

  if (!candidate) return null;
  return {
    fit: candidate,
    act: () => {
      trackClient('bm_upsell_acted', {
        surface,
        from: gameId,
        to: candidate.id,
        players: playerCount,
      });
      send({ type: 'SWITCH_GAME', gameId: candidate.id });
    },
    dismiss: () => setDismissed(true),
  };
}

/**
 * One label for every surface — the table hears the same offer twice, worded
 * once. The leading "Play" came off on 2026-08-30 (issue #29): with the row
 * reshaped the offer is a pill in an action row, where the verb is implied, and
 * the shorter string is what stops it wrapping to two lines at 390px. Changing
 * it here changes the lobby moment and the END action together, which is the
 * point of the shared function.
 */
export function biggerShowLabel(fit: GameFit, playerCount: number): string {
  return `${fit.name} — ${playerCount} is enough`;
}

/**
 * BiggerShowCTA — the lobby moment, shared by the phone and desktop lobbies
 * (one component, never two near-copies — same rule as StallNudge). The
 * narrator's line in display gold above the offer, and a quiet way out: this is
 * an invitation, not a nag.
 */
export function BiggerShowCTA({
  code,
  gameId,
  playerCount,
  isHost,
  maxWidth,
  style,
}: {
  code: string;
  gameId: string;
  playerCount: number;
  isHost: boolean;
  maxWidth?: number;
  /** Extra root styles (surface spacing/stacking) — only applied when the CTA
   *  actually renders, so a hidden CTA costs zero layout. */
  style?: React.CSSProperties;
}) {
  const offer = useBiggerShow({ code, gameId, playerCount, isHost, surface: 'lobby' });
  if (!offer) return null;

  return (
    <div
      className="rm-bigger-show"
      style={{
        width: '100%',
        maxWidth,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--space-2)',
        textAlign: 'center',
        ...style,
      }}
    >
      {/* The narrator's aside — same display/gold ramp as the stall nudge's line. */}
      <p
        style={{
          margin: 0,
          fontFamily: 'var(--font-display)',
          fontSize: 17,
          letterSpacing: '0.04em',
          color: 'var(--gold-5, #e6b866)',
        }}
      >
        Your table is big enough for the full show.
      </p>
      <Button variant="secondary" onClick={offer.act}>
        {biggerShowLabel(offer.fit, playerCount)}
      </Button>
      {/* The way out, in the shared quiet treatment (ui/QuietAction.tsx) — one
          primary, one secondary, one quiet, on every surface (issue #29). */}
      <QuietAction onClick={offer.dismiss}>Not tonight</QuietAction>
      <style>{`
        .rm-bigger-show { animation: rm-bigger-show-rise 480ms var(--ease-standard) both; }
        @keyframes rm-bigger-show-rise {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .rm-bigger-show { animation: none; }
        }
      `}</style>
    </div>
  );
}
